// Carriage (delivery method + cost + window) — lives in a pop-up opened from
// the line-items header so the main Quote Builder stays compact.
import { useState } from "react";
import { createPortal } from "react-dom";
import { Truck, X } from "lucide-react";
import { T2Label } from "../_t2";
import { DELIVERY_BANDS, suggestDeliveryBand, GBP } from "../_quoteHelpers";

export function CarriageFields({ delivery, onChange, subtotal }) {
  const suggestedBand = suggestDeliveryBand(subtotal);
  const method = normMethod(delivery.method);
  const isManual = method === "Collection" || method === "3rd party";
  const patch = (p) => onChange({ ...delivery, ...p });
  return (
    <div data-testid="carriage-fields">
      <T2Label>Method</T2Label>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {["Our", "Collection", "3rd party"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              const np = { ...delivery, method: m };
              if (m === "Our") np.cost = delivery.band ? (DELIVERY_BANDS.find((b) => b.key === delivery.band)?.cost ?? 0) : 0;
              if (m === "Collection") { np.cost = 0; np.band = ""; }
              if (m === "3rd party") { np.cost = delivery.cost || 0; np.band = ""; }
              onChange(np);
            }}
            className={`t2-chip ${method === m ? "orange" : ""}`}
            style={{ cursor: "pointer", flex: 1, padding: "6px 10px", justifyContent: "center", fontSize: 10.5 }}
            data-testid={`delivery-method-${m.replace(/\s+/g, "-").toLowerCase()}`}
          >{m}</button>
        ))}
      </div>

      {isManual ? (
        <>
          <T2Label>{method === "Collection" ? "Collection charge (£)" : "Third-party charge (£)"}</T2Label>
          <input type="number" step="0.01" min="0" className="t2-input t2-mono"
            value={delivery.cost ?? 0}
            onChange={(e) => patch({ cost: Number(e.target.value) || 0 })}
            placeholder={method === "Collection" ? "0.00 — customer collects" : "e.g. 65.00"}
            data-testid="delivery-manual-cost" />
          {method === "3rd party" && (
            <div style={{ marginTop: 8 }}>
              <T2Label>Carrier</T2Label>
              <input className="t2-input" value={delivery.carrier || ""}
                onChange={(e) => patch({ carrier: e.target.value })}
                placeholder="Palletforce, Tuffnells, DPD…"
                data-testid="delivery-carrier" />
            </div>
          )}
        </>
      ) : (
        <>
          <T2Label>Delivery band</T2Label>
          <select className="t2-input" value={delivery.band || ""}
            data-empty={!delivery.band ? "true" : "false"}
            onChange={(e) => {
              const b = DELIVERY_BANDS.find((x) => x.key === e.target.value);
              patch({ band: b?.key || "", cost: b ? b.cost : (delivery.cost || 0) });
            }}
            data-testid="delivery-band">
            <option value="">Select…</option>
            {DELIVERY_BANDS.map((b) => (
              <option key={b.key} value={b.key}>{b.label} — {b.cost === 0 ? "FREE" : `£${b.cost}`}</option>
            ))}
          </select>
          {suggestedBand && (!delivery.band || delivery.band !== suggestedBand.key) && (
            <button type="button" className="t2-mono"
              style={{ fontSize: 10, marginTop: 4, background: "none", border: "none", color: "var(--t2-teal-ink)", cursor: "pointer" }}
              onClick={() => patch({ band: suggestedBand.key, cost: suggestedBand.cost })}
              data-testid="delivery-suggest">
              Suggested: {suggestedBand.label} ({suggestedBand.cost === 0 ? "FREE" : `£${suggestedBand.cost}`})
            </button>
          )}
          <div style={{ marginTop: 8 }}>
            <T2Label>Manual override (£)</T2Label>
            <input type="number" step="0.01" min="0" className="t2-input t2-mono"
              value={delivery.cost ?? 0}
              onChange={(e) => patch({ cost: Number(e.target.value) || 0 })}
              data-testid="delivery-our-cost" />
          </div>
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <T2Label>Delivery window</T2Label>
        <input className="t2-input" value={delivery.deliveryWindow || ""}
          onChange={(e) => patch({ deliveryWindow: e.target.value })}
          placeholder="e.g. Fri AM before 11am"
          data-testid="delivery-window" />
      </div>
    </div>
  );
}

// Legacy methods (e.g. "Kerbside") are our own delivery.
const normMethod = (m) => (m === "Collection" || m === "3rd party" ? m : "Our");

export function carriageSummary(delivery) {
  const method = normMethod(delivery?.method);
  const cost = Number(delivery?.cost) || 0;
  const band = DELIVERY_BANDS.find((b) => b.key === delivery?.band);
  if (method === "Collection") return cost ? `Collection · ${GBP(cost)}` : "Collection";
  if (method === "Our" && !band && !cost) return "Not set";
  return cost === 0 ? "FREE" : GBP(cost);
}

export default function CarriageButton({ delivery, onChange, subtotal, testid = "btn-carriage" }) {
  const [open, setOpen] = useState(false);
  const unset = carriageSummary(delivery) === "Not set";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="t2-btn-ghost" data-testid={testid}
        style={{ padding: "4px 10px", fontSize: 11, ...(unset ? { borderColor: "var(--t2-orange)", color: "var(--t2-orange-ink)" } : {}) }}
        title="Delivery method, band, cost and window">
        <Truck className="w-3.5 h-3.5" /> Carriage · <span className="t2-mono" data-testid={`${testid}-summary`}>{carriageSummary(delivery)}</span>
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="carriage-modal">
          <div className="t2-panel" style={{ width: "100%", maxWidth: 420, padding: 18 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Truck className="w-4 h-4" style={{ color: "var(--t2-orange)" }} />
              <span className="t2-mono" style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--t2-white)" }}>Carriage</span>
              <span className="t2-mono" style={{ marginLeft: "auto", fontSize: 12, color: "var(--t2-orange-ink)", fontWeight: 800 }}>{carriageSummary(delivery)}</span>
              <button type="button" onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }} data-testid="carriage-close" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <CarriageFields delivery={delivery} onChange={onChange} subtotal={subtotal} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" className="t2-btn-primary" onClick={() => setOpen(false)} data-testid="carriage-done">Done</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
